import { Cardinality16D } from '@enums/Cardinality16D'
import proj4 from 'proj4'
// import { getEnumValues } from './EnumHelper'

export interface Datum {
  id: string
  label: string
  proj4: string
}
export const availableDatums: Datum[] = [
  { id: 'WGS84', label: 'WGS84', proj4: '+ellps=WGS84 +datum=WGS84' },
  { id: 'GRS80', label: 'ETRS89', proj4: '+ellps=GRS80' }
  // { id: 'NAD83', label: 'NAD83', proj4: '+ellps=GRS80 +datum=NAD83' }
]

interface Direction {
  name: string
  min: number
  max: number
}

export class Huso {
  private huso: number
  cardinal: 'N' | 'S'

  constructor(huso: number, cardinal: 'N' | 'S') {
    this.huso = huso
    this.cardinal = cardinal
  }

  getName() {
    return `${this.huso}${this.cardinal}`
  }

  getHuso() {
    return this.cardinal === 'N' ? this.huso : this.huso * -1
  }
}

export const availableHusos: Huso[] = [
  new Huso(28, 'N'),
  new Huso(29, 'N'),
  new Huso(30, 'N'),
  new Huso(31, 'N')
]

// export enum AvailableHusos {
//   H29 = 29,
//   H30 = 30,
//   H31 = 31
// }
// const availableHusos = getEnumValues(AvailableHusos)

export class CoordinateHelper {
  /* static distanceBetweenPoints (x1, y1, x2, y2) {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
  } */

  static distanceBetweenPoints(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) {
    const R = 6371 // Radius of the earth in km
    const dLat: number = this.deg2rad(lat2 - lat1) // deg2rad below
    const dLon: number = this.deg2rad(lon2 - lon1)
    const a: number =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)

    const c: number = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const d: number = R * c // Distance in km
    return d
  }

  static deg2rad(deg: number) {
    return deg * (Math.PI / 180)
  }

  static DDToDMS(D: number, isLongitude: boolean) {
    return {
      dir: D < 0 ? (isLongitude ? 'W' : 'S') : isLongitude ? 'E' : 'N',
      deg: 0 | (D < 0 ? (D = -D) : D),
      min: 0 | (((D += 1e-9) % 1) * 60),
      sec: (0 | (((D * 60) % 1) * 6000)) / 100
    }
  }

  static DMSToDD(cardinal: string, deg: number, min: number, sec: number) {
    let dd = deg + min / 60 + sec / 3600

    if (cardinal === 'W' || cardinal === 'S') {
      dd = dd * -1
    }

    return dd
  }

  static WGS84ToH30N(lat: number, lon: number): number[] {
    return CoordinateHelper.WGS84ToHusoNumber(lat, lon, 30)
  }

  private static WGS84ToHusoNumber(
    lat: number,
    lon: number,
    huso: number
  ): number[] {
    return this.WGS84ToHuso(
      lat,
      lon,
      availableHusos.find((x) => x.getHuso() === huso)!
    )
  }

  static WGS84ToHuso(lat: number, lon: number, huso: Huso): number[] {
    const projection = `+proj=utm +zone=${huso.getHuso()} +datum=WGS84 +units=m +no_defs`
    return proj4(projection, [lon, lat])
  }

  //------

  static convertLatLonDatum(
    lat: number,
    lon: number,
    fromDatum: Datum,
    toDatum: Datum
  ) {
    const fromProjection = `+proj=longlat ${fromDatum.proj4} +no_defs`
    const toProjection = `+proj=longlat ${toDatum.proj4} +no_defs`
    return proj4(fromProjection, toProjection, [lon, lat]).reverse()
  }

  static LatLonToUTM(
    lat: number,
    lon: number,
    datum: Datum,
    huso: Huso
  ): number[] {
    const fromProjection = `+proj=longlat ${datum.proj4} +units=degrees`
    const toProjection = `+proj=utm +zone=${huso.getHuso()} ${
      datum.proj4
    } +units=m +no_defs`
    return proj4(fromProjection, toProjection, [lon, lat])
  }

  static UTMToLatLon(x: number, y: number, datum: Datum, huso: Huso): number[] {
    const fromProjection = `+proj=utm +zone=${huso.getHuso()} ${
      datum.proj4
    } +units=m +no_defs`
    const toProjection = `+proj=longlat ${datum.proj4} +units=degrees`
    return proj4(fromProjection, toProjection, [x, y]).reverse()
  }

  private static findDirection(directions: Direction[], angle: number) {
    const normalizedAngle = ((angle % 360) + 360) % 360
    return directions.find(
      (d) => normalizedAngle >= d.min && normalizedAngle < d.max
    )
  }

  /**
   * Retorna un texto que describe la dirección aproximada
   * (por ejemplo, "NE (22.5º-67.5º)") basado en un ángulo en grados [0..360).
   */
  static getDirectionWithRange(angle: number) {
    const directions = [
      { name: 'N', min: 337.5, max: 360 },
      { name: 'N', min: 0, max: 22.5 },
      { name: 'NE', min: 22.5, max: 67.5 },
      { name: 'E', min: 67.5, max: 112.5 },
      { name: 'SE', min: 112.5, max: 157.5 },
      { name: 'S', min: 157.5, max: 202.5 },
      { name: 'SW', min: 202.5, max: 247.5 },
      { name: 'W', min: 247.5, max: 292.5 },
      { name: 'NW', min: 292.5, max: 337.5 }
    ]
    const dir = this.findDirection(directions, angle)
    // Si no coincide con ninguna, asumimos 'N' por defecto
    const directionName = dir ? dir.name : 'N'
    const minAngle = dir ? this.dToFixed(dir.min) : 0
    const maxAngle = dir ? this.dToFixed(dir.max) : 0

    // Formateamos la salida: "NE (22.5º-67.5º)"
    return `${directionName} (${minAngle}º-${maxAngle}º)`
  }

  static getDirectionWithRangePrecision(angle: number) {
    const table = Cardinality16D.getTable()
    const entry = this.findDirection(table, angle)
    return entry
  }

  // Función de ayuda para formatear un número a un decimal o similar
  static dToFixed(num: number) {
    // Quita decimales innecesarios o ajusta según tu preferencia
    return parseFloat(num.toFixed(1))
  }
}
